import { createPortal } from 'react-dom';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/react';
import { Icon } from '../../components/Icon';
import { donationPayPal, donationTransfer, SUPPORT_EMAIL } from './config';
import { buildContactMailto } from './mailto';
import { hasContactBackend, submitContact } from './repository';
import { appHref, hrefFor } from '../../routing/browser';

type SupportDialogProps =
  | { mode: 'contact'; onClose: () => void }
  | { mode: 'donate'; onClose: () => void };

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SupportDialog({ mode, onClose }: SupportDialogProps) {
  const tr = useTranslation();
  const isContact = mode === 'contact';
  const titleId = isContact ? 'contact-dialog-title' : 'donate-dialog-title';
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    const isVisible = (element: HTMLElement | null): element is HTMLElement => Boolean(element?.isConnected && element.getClientRects().length > 0);
    const fallbackFocus = document.querySelector<HTMLElement>('.navigation-toggle');
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector<HTMLButtonElement>('.icon-button')?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = previousOverflow;
      const focusTarget = isVisible(previousFocus) ? previousFocus : (isVisible(fallbackFocus) ? fallbackFocus : null);
      focusTarget?.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(<div className="dialog-backdrop support-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section ref={dialogRef} className={`support-dialog ${isContact ? 'contact-dialog' : 'donate-dialog'}`} role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={event => {
      if (event.key !== 'Tab') return;
      const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button, input, select, textarea, a[href], [tabindex]')].filter(control => !control.matches(':disabled') && control.tabIndex >= 0 && control.getClientRects().length > 0);
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }}>
      <div className="support-dialog-head">
        <div>
          <p className="eyebrow">{tr(isContact ? 'LIÊN HỆ' : 'ỦNG HỘ')}</p>
          <h2 id={titleId}>{tr(isContact ? 'Liên hệ DesignForge' : 'Ủng hộ DesignForge')}</h2>
        </div>
        <button type="button" className="icon-button" onClick={onClose} aria-label={tr(isContact ? 'Đóng hộp thoại liên hệ' : 'Đóng hộp thoại Donate')}><Icon name="close" /></button>
      </div>
      {isContact ? <ContactForm onClose={onClose} /> : <DonateContent />}
    </section>
  </div>, document.body);
}

function ContactForm({ onClose }: { onClose: () => void }) {
  const tr = useTranslation();
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState('');
  const [website, setWebsite] = useState('');
  const submissionId = useRef('');
  const backend = hasContactBackend();
  const edited = () => { setError(''); submissionId.current = ''; };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || receipt) return;
    if (description.trim().length < 5) { setError(tr('Vui lòng mô tả ít nhất 5 ký tự.')); return; }
    if (!emailPattern.test(email.trim())) { setError(tr('Email chưa hợp lệ')); return; }
    setError('');
    if (!backend) { window.location.href = buildContactMailto(description, email); return; }
    setBusy(true);
    try {
      submissionId.current ||= crypto.randomUUID();
      const result = await submitContact({ id: submissionId.current, email: email.trim(), description: description.trim(), website });
      setReceipt(result.id);
    } catch (cause) {
      setError(tr(cause instanceof Error && cause.message === 'rate_limited' ? 'Bạn đã gửi nhiều yêu cầu. Vui lòng thử lại sau hoặc liên hệ qua email.' : 'Chưa xác nhận được yêu cầu. Thử lại sẽ không tạo bản trùng; bạn cũng có thể liên hệ qua email.'));
    } finally { setBusy(false); }
  };

  if (receipt) return <div className="support-form" role="status"><p className="support-lead">{tr('Đã nhận yêu cầu của bạn.')}</p><p className="support-copy">{tr('Yêu cầu đã được lưu vào hộp thư DesignForge. Mình sẽ xem và phản hồi qua email bạn cung cấp. Website chưa gửi email xác nhận tự động.')}</p><p className="support-mail-note">{tr('Mã yêu cầu')}: {receipt}</p><div className="support-dialog-actions"><button type="button" className="primary-button" onClick={onClose}>{tr('Đóng')}</button></div></div>;

  return <form className="support-form" onSubmit={submit} noValidate>
    <p className="support-lead">{tr('Gửi ý kiến hoặc yêu cầu')}</p>
    <p className="support-copy">{tr('Bạn cứ mô tả điều muốn góp ý hoặc tính năng cần bổ sung. Mình sẽ đọc và liên hệ lại qua email khi tiếp nhận.')}</p>
    <label htmlFor="support-description">{tr('Mô tả ý kiến / yêu cầu')} <span aria-hidden="true">*</span></label>
    <textarea id="support-description" required minLength={5} maxLength={5000} disabled={busy} rows={5} value={description} onChange={event => { setDescription(event.target.value); edited(); }} placeholder={tr('Ví dụ: mình muốn có thêm công cụ...')} />
    <label htmlFor="support-email">{tr('Email của bạn')} <span aria-hidden="true">*</span></label>
    <input id="support-email" required type="email" maxLength={254} disabled={busy} value={email} onChange={event => { setEmail(event.target.value); edited(); }} placeholder="you@example.com" autoComplete="email" />
    <div className="support-honeypot" aria-hidden="true"><label htmlFor="support-website">Website<input id="support-website" tabIndex={-1} autoComplete="off" value={website} onChange={event => setWebsite(event.target.value)} /></label></div>
    <p className="support-mail-note">{tr(backend ? 'Khi gửi, email và nội dung được lưu riêng tư để chủ website xử lý yêu cầu. Không gửi thông tin nhạy cảm.' : 'Nút gửi sẽ mở ứng dụng email của bạn.')} {backend && <a href={hrefFor('privacy')}>{tr('Chính sách riêng tư')}</a>}</p>
    {error && <p className="support-error" role="alert">{error}</p>}
    <p className="support-mail-note"><a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
    <div className="support-dialog-actions"><button type="button" className="secondary-button" onClick={onClose}>{tr('Đóng')}</button><button type="submit" className="primary-button" disabled={busy}><Icon name="arrow" size={17} />{tr(busy ? 'Đang gửi…' : 'Gửi yêu cầu')}</button></div>
  </form>;
}

function DonateContent() {
  const tr = useTranslation();
  const [showTransfer, setShowTransfer] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'account' | 'note' | 'error' | null>(null);
  const copyTransfer = async (field: 'account' | 'note') => {
    try {
      await navigator.clipboard.writeText(field === 'account' ? donationTransfer.accountNumber : donationTransfer.transferNote);
      setCopyStatus(field);
    } catch { setCopyStatus('error'); }
  };

  return <div className="support-donate-content">
    <p className="support-lead">{tr('Cảm ơn bạn đã sử dụng DesignForge.')}</p>
    <p className="support-copy">{tr('Mình sẽ cố gắng thật nhiều để cải thiện website. Niềm vui của mình là thấy bạn ghé thăm và sử dụng những công cụ này mỗi ngày. Chúc bạn luôn nhiều năng lượng, công việc thuận lợi và thật khỏe mạnh.')}</p>
    <p className="support-copy">{tr('Nếu DesignForge giúp bạn tiết kiệm được một chút thời gian, bạn có thể ủng hộ vibe coder lỏ này một gói mì tôm chống đói để tiếp tục phát triển web nhé. Một lời cảm ơn của bạn cũng đã là động lực rất lớn rồi!')}</p>
    <div className="donate-methods" aria-label={tr('Cách ủng hộ')}>
      <button type="button" className="donate-method donate-method-bank" aria-expanded={showTransfer} aria-controls="donate-bank-details" onClick={() => { setShowTransfer(value => !value); setCopyStatus(null); }}>
        <span className="donate-method-icon"><Icon name="scan" size={21}/></span><span className="donate-method-copy"><strong>{tr('Chuyển khoản Việt Nam')}</strong><small>{tr('Quét VietQR hoặc chuyển khoản')}</small></span><Icon name="arrow" size={17}/>
      </button>
      <a className="donate-method donate-paypal-link" href={donationPayPal.url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
        <span className="donate-method-icon donate-paypal-mark" aria-hidden="true">P</span><span className="donate-method-copy"><strong>{tr('Ủng hộ qua PayPal')}</strong><small>{tr('Quốc tế · Tự chọn số tiền')}</small></span><Icon name="external" size={17}/>
      </a>
    </div>
    {showTransfer && <div id="donate-bank-details" className="donate-transfer-panel">
      <div className="donate-qr"><a href={appHref(donationTransfer.qrAsset)} target="_blank" rel="noopener noreferrer" aria-label={tr('Mở mã VietQR kích thước đầy đủ')}><img src={appHref(donationTransfer.qrAsset)} width={850} height={1100} alt={tr('Mã VietQR ủng hộ DesignForge qua MB Bank')}/></a><a className="donate-qr-download" href={appHref(donationTransfer.qrAsset)} download="DesignForge-VietQR.png">{tr('Tải mã QR')}</a></div>
      <div className="donate-bank-copy"><dl><div><dt>{tr('Ngân hàng')}</dt><dd>{donationTransfer.bankName}</dd></div><div><dt>{tr('Chủ tài khoản')}</dt><dd>{donationTransfer.accountName}</dd></div><div><dt>{tr('Số tài khoản')}</dt><dd className="donate-account-number">{donationTransfer.accountNumber}</dd></div><div><dt>{tr('Nội dung chuyển khoản')}</dt><dd>{donationTransfer.transferNote}</dd></div></dl>
        <div className="donate-copy-actions"><button type="button" className="donate-copy-account" onClick={() => void copyTransfer('account')}>{tr('Sao chép số tài khoản')}</button><button type="button" className="donate-copy-note" onClick={() => void copyTransfer('note')}>{tr('Sao chép nội dung')}</button></div>
        <p className={`donate-copy-feedback ${copyStatus === 'error' ? 'is-error' : ''}`} role={copyStatus === 'error' ? 'alert' : 'status'}>{copyStatus && tr(copyStatus === 'error' ? 'Không sao chép được. Bạn có thể chọn và sao chép thông tin bên trên.' : copyStatus === 'account' ? 'Đã sao chép số tài khoản.' : 'Đã sao chép nội dung chuyển khoản.')}</p>
      </div>
    </div>}
    <p className="support-contact-note">{tr('Email liên hệ')}: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
  </div>;
}
